import { computed } from 'vue';
import { useCashbackStore } from '@/stores/cashback';
import type { CashbackHistoryItem, NullCashbackHistoryFilters } from '@/types/cashback';
import { useToast } from 'primevue/usetoast';

export const useCashback = () => {
    const cashbackStore = useCashbackStore();

    const toast = useToast();
    const toastGetCashback = 'get_cashback';
    const toastGetCashbackHistory = 'get_cashback_history';
    const toastGetCashbackSettings = 'get_cashback_settings';

    // Computed properties
    const cashback = computed(() => cashbackStore.cashback);
    const cashbackHistory = computed(() => cashbackStore.cashbackHistory);
    const cashbackSettings = computed(() => cashbackStore.cashbackSettings);
    const loadingFlag = computed(() => cashbackStore.loadingFlag);
    const loadingHistoryFlag = computed(() => cashbackStore.loadingHistoryFlag);
    const loadingSettingsFlag = computed(() => cashbackStore.loadingSettingsFlag);
    const getCashbackModal = computed(() => cashbackStore.getCashbackModal);
    // Computed properties for cashback info
    const currentLevelPercentage = computed(() => {
        return cashback.value?.current_level_info?.percentage || 0;
    });
    function toSetGetCashbackModal(flag: boolean, cashback: CashbackHistoryItem | null) {
        cashbackStore.toSetGetCashbackModal(flag, cashback);
    }
    const nextLevelPercentage = computed(() => {
        return cashback.value?.next_level_info?.percentage || 0;
    });

    const pendingCashbackAmount = computed(() => {
        return cashback.value?.pending_cashback_amount || 0;
    });

    const realBalanceNetLoss = computed(() => {
        return cashback.value?.real_balance_net_loss || 0;
    });

    const progressToNextLevel = computed(() => {
        if (!cashback.value?.next_level_info?.min_bet_sum_amount) return 0;

        const spent = cashback.value.real_balance_spent || 0;
        const nextLevelMin = cashback.value.next_level_info.min_bet_sum_amount;

        return Math.min((spent / nextLevelMin) * 100, 100);
    });

    const isMaxLevel = computed(() => {
        return nextLevelPercentage.value === 0 || currentLevelPercentage.value >= nextLevelPercentage.value;
    });

    // Computed properties for cashback settings
    const isCashbackEnabled = computed(() => {
        return cashbackSettings.value?.is_enabled || false;
    });

    const cashbackLevels = computed(() => {
        return cashbackSettings.value?.levels || [];
    });

    const cashbackSchedule = computed(() => {
        return cashbackSettings.value?.schedule;
    });

    const playerCashbackCurrency = computed(() => {
        return cashbackSettings.value?.currency;
    });

    const allCashbackCurrencies = computed(() => {
        return cashbackSettings.value?.all_currencies;
    });

    const highestCashbackLevel = computed(() => {
        if (!cashbackLevels.value.length) return null;
        return cashbackLevels.value.reduce((max, level) => (level.percentage > max.percentage ? level : max));
    });

    const nextPayoutInfo = computed(() => {
        if (!cashbackSchedule.value) return null;

        const now = new Date();
        const { frequency, payout_day, payout_hour } = cashbackSchedule.value;

        const nextPayout = new Date();

        if (frequency === 'daily') {
            nextPayout.setHours(payout_hour, 0, 0, 0);
            if (nextPayout <= now) {
                nextPayout.setDate(nextPayout.getDate() + 1);
            }
        } else if (frequency === 'weekly') {
            // Конвертируем API день недели (1=понедельник) в JS день недели (1=понедельник, 0=воскресенье)
            const jsDay = payout_day === 7 ? 0 : payout_day; // Воскресенье в API = 7, в JS = 0
            const currentDay = now.getDay();
            const daysUntilPayout = (jsDay - currentDay + 7) % 7;

            nextPayout.setDate(now.getDate() + daysUntilPayout);
            nextPayout.setHours(payout_hour, 0, 0, 0);

            // Если время уже прошло сегодня, то следующая выплата через неделю
            if (nextPayout <= now) {
                nextPayout.setDate(nextPayout.getDate() + 7);
            }
        } else if (frequency === 'monthly') {
            nextPayout.setDate(payout_day);
            nextPayout.setHours(payout_hour, 0, 0, 0);
            if (nextPayout <= now) {
                nextPayout.setMonth(nextPayout.getMonth() + 1);
            }
        }

        return nextPayout;
    });

    // Форматированное время до следующей выплаты кешбека
    const timeUntilNextPayout = computed(() => {
        if (!nextPayoutInfo.value) return null;

        const now = new Date();
        const diff = nextPayoutInfo.value.getTime() - now.getTime();

        if (diff <= 0) return null;

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        return { days, hours, minutes };
    });

    // Проверка: показывать ли кешбек (меньше 3 дней до выплаты)
    const shouldShowCashbackPromo = computed(() => {
        if (!timeUntilNextPayout.value) return false;
        return timeUntilNextPayout.value.days < 3;
    });

    // Ожидаемая сумма выплаты кешбека
    const expectedCashbackPayout = computed(() => {
        return pendingCashbackAmount.value || realBalanceNetLoss.value * (currentLevelPercentage.value / 100) || 0;
    });

    // Actions
    const toGetCashback = async (): Promise<void> => {
        try {
            await cashbackStore.awaitToGetCashback();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetCashback, detail: error, life: 4000 });
            throw error;
        }
    };

    const toGetCashbackHistory = async (filters: NullCashbackHistoryFilters): Promise<void> => {
        try {
            await cashbackStore.awaitToGetCashbackHistory(filters);
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetCashbackHistory, detail: error, life: 4000 });
            throw error;
        }
    };

    const toResetCashback = (): void => {
        cashbackStore.toResetCashback();
    };

    const toGetCashbackSettings = async (): Promise<void> => {
        try {
            await cashbackStore.awaitToGetCashbackSettings();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetCashbackSettings, detail: error, life: 4000 });
            throw error;
        }
    };

    const ensureCashbackSettingsLoaded = async (): Promise<void> => {
        try {
            await cashbackStore.ensureCashbackSettingsLoaded();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetCashbackSettings, detail: error, life: 4000 });
            throw error;
        }
    };

    const toResetCashbackHistory = (): void => {
        cashbackStore.toResetCashbackHistory();
    };

    const toResetCashbackSettings = (): void => {
        cashbackStore.toResetCashbackSettings();
    };

    return {
        // State
        cashback,
        cashbackHistory,
        cashbackSettings,
        loadingFlag,
        loadingHistoryFlag,
        loadingSettingsFlag,
        getCashbackModal,
        // Computed
        currentLevelPercentage,
        nextLevelPercentage,
        pendingCashbackAmount,
        realBalanceNetLoss,
        progressToNextLevel,
        isMaxLevel,

        // Settings computed
        isCashbackEnabled,
        cashbackLevels,
        cashbackSchedule,
        playerCashbackCurrency,
        allCashbackCurrencies,
        highestCashbackLevel,
        nextPayoutInfo,

        // Cashback promo computed
        timeUntilNextPayout,
        shouldShowCashbackPromo,
        expectedCashbackPayout,

        // Actions
        toGetCashback,
        toGetCashbackHistory,
        toGetCashbackSettings,
        toResetCashback,
        toResetCashbackHistory,
        toResetCashbackSettings,
        ensureCashbackSettingsLoaded,
        toSetGetCashbackModal
    };
};
